import { FunctionComponent, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Typography, AppBar, Toolbar, IconButton, Dialog, Tooltip, Box } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import TestIcon from '@mui/icons-material/PlayCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import massActionSelectors from 'src/selectors/massAction';
import TestForm from 'src/components/common/TestForm';
import ActionType from 'src/enums/actionType';
import { createSelector } from '@reduxjs/toolkit';

// Prefer this Toolbar over MessageActionAppBar
interface ToolbarActionsProps {
  actions: {
    onSave: () => void;
    onSend: () => void;
    showTestForm: (show: boolean) => void;
  };
  flowData: {
    contentValidity: { isValid: boolean };
    isNewContent: boolean;
    hasChanges: boolean;
    diffusionTypeIsEmpty: boolean;
  };
  actionType: number;
  isWritable: boolean;
}

const ToolbarActions: FunctionComponent<ToolbarActionsProps> = ({
  actions,
  flowData,
  actionType,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <Box
        sx={{
          boxShadow: 'inset -0.3px 0px 0px 0px #ffffffd5',
          padding: '0 8px',
        }}
      />
      <Box
        sx={{
          boxShadow: 'inset -0.3px 0px 0px 0px #ffffffd5',
          padding: '0 8px',
        }}
      >
        <Tooltip title={t('smartmessaging.massAction.navButton.saveActionMessage')}>
          <span>
            <IconButton
              aria-label="clear"
              disabled={
                !flowData.contentValidity?.isValid ||
                (!flowData.isNewContent && !flowData.hasChanges) ||
                !flowData.hasChanges
              }
              sx={{
                color: 'primary.contrastText',
                backgroundColor: 'primary.light',
                '&.Mui-disabled': {
                  opacity: 0.7,
                },
              }}
              onClick={e => {
                e.preventDefault();
                actions.onSave();
              }}
              size="large"
            >
              <SaveIcon color="secondary" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      {![
        ActionType.MEMBER_NOTIFICATION.id,
        ActionType.PUSH_XPLORACTIVE.id,
        ActionType.PROSPECTING.id,
        ActionType.XPLORACTIVE_NEWS.id,
        ActionType.SMS.id,
      ].includes(actionType) && (
        <div
          style={{
            boxShadow: 'inset -0.3px 0px 0px 0px #ffffffd5',
            padding: '0 8px',
          }}
        >
          <Tooltip title={t('smartmessaging.massAction.navButton.testContent')}>
            <span>
              <IconButton
                aria-label="clear"
                sx={{
                  color: 'primary.contrastText',
                  backgroundColor: 'primary.light',
                  '&.Mui-disabled': {
                    opacity: 0.7,
                  },
                }}
                disabled={flowData.isNewContent || flowData.hasChanges}
                onClick={e => {
                  e.preventDefault();
                  actions.showTestForm(true);
                }}
                size="large"
              >
                <TestIcon color="secondary" />
              </IconButton>
            </span>
          </Tooltip>
        </div>
      )}
      <Box
        sx={{
          padding: '0 8px',
        }}
      >
        <Tooltip title={t('smartmessaging.massAction.navButton.send')}>
          <span>
            <IconButton
              aria-label="clear"
              sx={{
                color: 'primary.contrastText',
                backgroundColor: 'primary.light',
                '&.Mui-disabled': {
                  opacity: 0.7,
                },
              }}
              disabled={
                !flowData.contentValidity?.isValid ||
                (![
                  ActionType.MEMBER_NOTIFICATION.id,
                  ActionType.PROSPECTING.id,
                  ActionType.XPLORACTIVE_NEWS.id,
                ].includes(actionType) &&
                  flowData.diffusionTypeIsEmpty) ||
                flowData.isNewContent ||
                flowData.hasChanges
              }
              onClick={e => {
                e.preventDefault();
                actions.onSend();
              }}
              size="large"
            >
              <SendIcon color="secondary" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </>
  );
};

interface MessageActionAppBarProps {
  actionType: number;
  onSave: () => void;
  onSend: () => void;
  contentValidity: any;
  hasChanges: boolean;
  diffusionTypeIsEmpty?: boolean;
  currentMassActionId: number;
  actionId: number | null;
  currentCampaignAction: any;
  isWritable: boolean;
}

const fieldsSelector = createSelector(
  massActionSelectors.getContentFields,
  contentFields => contentFields
);

function MessageActionAppBar({
  actionType,
  onSave,
  onSend,
  contentValidity,
  hasChanges,
  diffusionTypeIsEmpty, // à passer en props, sans redux
  currentMassActionId,
  actionId,
  currentCampaignAction,
  isWritable,
}: MessageActionAppBarProps) {
  const [testFormIsVisible, showTestForm] = useState(false);
  const { t } = useTranslation();

  const { currentName } = useSelector(({ massAction }) => massAction);
  const contentFields = useSelector(fieldsSelector);
  const currentAppLn = useSelector(({ app: { currentLanguage } }) => currentLanguage);

  return (
    <>
      <Dialog open={testFormIsVisible} fullWidth>
        <AppBar
          sx={{
            position: 'relative',
            textAlign: 'left',
          }}
          color="primary"
        >
          <Toolbar>
            <Typography variant="h5" color="inherit" style={{ flex: 1 }}>
              {t('smartmessaging.campaignEditor.testAction.popupTitle')}
            </Typography>
            <IconButton
              aria-label="clear"
              color="inherit"
              onClick={e => {
                e.preventDefault();
                showTestForm(false);
              }}
              size="large"
            >
              <CloseIcon color="secondary" />
            </IconButton>
          </Toolbar>
        </AppBar>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            minHeight: '300px',
          }}
        >
          <TestForm
            forcedFields={{ field0: 'INTEGER', ...contentFields }}
            includeWizVille
            campaignActionId={(currentCampaignAction && currentCampaignAction.id) || actionId}
            actionTypeId={actionType}
            currentAppLn={currentAppLn}
          />
        </Box>
      </Dialog>
      <AppBar
        sx={{
          position: 'relative',
          textAlign: 'left',
        }}
        color="primary"
      >
        <Toolbar>
          <Box
            sx={{
              flex: 1,
            }}
          >
            <Typography variant="h5" color="inherit">
              {t(`smartmessaging.contentEditor.title.${actionType}`)}
            </Typography>
            <Typography variant="body2" color="inherit">
              {currentName}
              {!!currentMassActionId && ` (${currentMassActionId})`}
            </Typography>
          </Box>
          <ToolbarActions
            actionType={actionType}
            flowData={{
              contentValidity,
              isNewContent: !actionId,
              hasChanges,
              diffusionTypeIsEmpty,
            }}
            actions={{ onSave, onSend, showTestForm }}
            isWritable={isWritable}
          />
        </Toolbar>
      </AppBar>
    </>
  );
}

export default MessageActionAppBar;
